import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { ArticleSearchCombobox } from './ArticleSearchCombobox';
import logo from '../../assets/logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuTopPx, setMenuTopPx] = useState(0);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastTopPx, setToastTopPx] = useState(0);
  const headerRootRef = useRef(null);
  const headerBarRef = useRef(null);
  const mobileMenuPanelRef = useRef(null);
  const navigate = useNavigate();

  const updateMenuTop = () => {
    const el = headerBarRef.current;
    if (!el) return;
    setMenuTopPx(el.getBoundingClientRect().bottom);
  };

  useLayoutEffect(() => {
    if (!isMenuOpen) return;
    updateMenuTop();
    const el = headerBarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateMenuTop);
    ro.observe(el);
    window.addEventListener('resize', updateMenuTop);
    window.addEventListener('scroll', updateMenuTop, { capture: true, passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateMenuTop);
      window.removeEventListener('scroll', updateMenuTop, true);
    };
  }, [isMenuOpen]);

  const updateToastTop = () => {
    const headerBottom = headerRootRef.current?.getBoundingClientRect().bottom ?? 64;
    const menuEl = mobileMenuPanelRef.current;
    const bottom =
      isMenuOpen && menuEl?.isConnected
        ? Math.max(headerBottom, menuEl.getBoundingClientRect().bottom)
        : headerBottom;
    setToastTopPx(bottom);
  };

  useLayoutEffect(() => {
    if (!toastMessage) return;
    updateToastTop();
    const headerEl = headerRootRef.current;
    const menuEl = mobileMenuPanelRef.current;
    const ro = new ResizeObserver(updateToastTop);
    if (headerEl) ro.observe(headerEl);
    if (menuEl) ro.observe(menuEl);
    window.addEventListener('resize', updateToastTop);
    window.addEventListener('scroll', updateToastTop, { capture: true, passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateToastTop);
      window.removeEventListener('scroll', updateToastTop, true);
    };
  }, [toastMessage, isMenuOpen]);

  useEffect(() => {
    if (!toastMessage) return;
    const id = window.setTimeout(() => setToastMessage(null), 5000);
    return () => window.clearTimeout(id);
  }, [toastMessage]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMenuOpen]);

  useEffect(() => {
    if (!toastMessage) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setToastMessage(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toastMessage]);

  const showToast = (message) => {
    const headerBottom = headerRootRef.current?.getBoundingClientRect().bottom ?? 64;
    const menuEl = mobileMenuPanelRef.current;
    const bottom =
      isMenuOpen && menuEl?.isConnected
        ? Math.max(headerBottom, menuEl.getBoundingClientRect().bottom)
        : headerBottom;
    setToastTopPx(bottom);
    setToastMessage(message);
  };

  const handlePickArticle = (path) => {
    setToastMessage(null);
    navigate(path);
    setSearchQuery('');
    setIsMenuOpen(false);
  };

  const handleEmptySearch = () => {
    showToast('Search is empty, Enter a keyword .');
  };

  const handleNoMatchesSearch = () => {
    showToast('No matching article. Try another keyword.');
  };

  const navLink =
    'rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-brand-50 hover:text-brand-900';

  const inputClass =
    'w-full rounded-md border border-brand-100 bg-white py-2 pl-3 pr-10 text-sm text-slate-800 shadow-sm shadow-brand-900/5 placeholder:text-slate-400 transition-[border,box-shadow] hover:border-brand-200 focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-400/35';

  const mobileMenu =
    isMenuOpen &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        ref={mobileMenuPanelRef}
        id="mobile-nav-dialog"
        className="fixed left-0 right-0 z-[100] overflow-visible border-b border-brand-100 bg-white px-4 py-3 shadow-lg sm:px-6 lg:px-8 md:hidden"
        style={{ top: menuTopPx > 0 ? menuTopPx : 56 }}
        role="dialog"
        aria-modal="false"
        aria-label="Main navigation"
      >
          <nav className="flex flex-col gap-0.5 rounded-lg bg-brand-50 p-2" aria-label="Main">
            <Link
              to="/"
              className={`${navLink} py-2.5`}
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`${navLink} py-2.5`}
              onClick={() => setIsMenuOpen(false)}
            >
              About
            </Link>
            <Link
              to="/articles"
              className={`${navLink} py-2.5`}
              onClick={() => setIsMenuOpen(false)}
            >
              Articles
            </Link>
            <Link
              to="/chief-editor"
              className={`${navLink} py-2.5`}
              onClick={() => setIsMenuOpen(false)}
            >
              Chief Editor
            </Link>
            <Link
              to="/contact"
              className={`${navLink} py-2.5`}
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>
          </nav>
          <ArticleSearchCombobox
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onPickArticle={handlePickArticle}
            onEmptySearch={handleEmptySearch}
            onNoMatchesSearch={handleNoMatchesSearch}
            inputClass={inputClass}
            placeholder="Search drugs, conditions…"
            formClassName="mt-3"
          />
        </div>,
      document.body
    );

  const toastPortal =
    toastMessage &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className="pointer-events-none fixed right-4 z-[200] sm:right-6"
        style={{ top: `${(toastTopPx > 0 ? toastTopPx : 64) + 12}px` }}
      >
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-auto flex max-w-md items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-lg shadow-slate-900/15 ring-1 ring-slate-900/5"
        >
          <p className="min-w-0 flex-1 pt-0.5 leading-snug">{toastMessage}</p>
          <button
            type="button"
            onClick={() => setToastMessage(null)}
            className="shrink-0 rounded-md p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
            aria-label="Dismiss notification"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
      </div>,
      document.body
    );

  return (
    <header
      ref={headerRootRef}
      className="sticky top-0 z-50 border-b border-slate-200 bg-white text-slate-800 shadow-md shadow-slate-900/10"
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div ref={headerBarRef} className="flex items-center gap-3 py-3.5 sm:gap-4">
          <div className="flex min-w-0 flex-1 justify-start">
            <Link to="/" className="shrink-0">
              <img
                src={logo}
                alt="MedIntel"
                className="h-12 w-auto sm:h-14"
              />
            </Link>
          </div>

          <nav className="hidden shrink-0 md:block" aria-label="Main">
            <ul className="flex items-center gap-1 lg:gap-2">
              <li>
                <Link to="/" className={navLink}>
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className={navLink}>
                  About
                </Link>
              </li>
              <li>
                <Link to="/articles" className={navLink}>
                  Articles
                </Link>
              </li>
              <li>
                <Link to="/chief-editor" className={navLink}>
                  Chief Editor
                </Link>
              </li>
              <li>
                <Link to="/contact" className={navLink}>
                  Contact
                </Link>
              </li>
            </ul>
          </nav>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
            <ArticleSearchCombobox
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onPickArticle={handlePickArticle}
              onEmptySearch={handleEmptySearch}
              onNoMatchesSearch={handleNoMatchesSearch}
              inputClass={inputClass}
              placeholder="Search…"
              formClassName="hidden w-56 shrink-0 md:block lg:w-72 xl:w-80"
            />

            <button
              type="button"
              className="inline-flex shrink-0 rounded-md p-2 text-slate-600 hover:bg-brand-50 hover:text-brand-900 md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-nav-dialog"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {mobileMenu}
        {toastPortal}
      </div>
    </header>
  );
};

export default Header;
